/* eslint-disable max-len */

import {
  cloneData, cloneDataOrNull, cloneListOfCloneable, DataModel, DataModelBuilder,
  isSameData, isSameList, SerializationContext, SerializedData,
} from '../dto/dto.js';
import * as dayjs from 'dayjs';
import { IEntity } from '../../components/core/entity.js';
import { ComponentList } from '../../components/core/component-list.js';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import { CustomerProfile } from './customer-profile.js';
import { ContactDetails } from './contact-details.js';
import { OnlinePaymentDetails } from './online-payment-details.js';
import { PaymentType } from './payment-type.js';
import { BusinessModuleRef } from './business-module-ref.js';
import { fromEnumAsString, fromEnumAsStringOrNull, toEnumFromString, toEnumFromStringOrNull } from '../../serialization/enum-utils.js';
import { PaymentStatus } from './payment-status.js';
import { LegacyCustomerOrderBase } from '../../contracts/customer-order.js';
import { ProductOrderItem } from './product-order-item.js';
import { LegacyOrderHook } from './legacy-order-hook.js';
import { VersionRange } from '../core/version-range.js';
import { Language } from './language.js';
import { getDefaultPaymentMethodForPaymentType, PaymentMethod } from './payment-method.js';
import { CustomerAccountBase } from './customer-account-base.js';
import { Price } from './price.js';
import { Refund } from './discounts/refund.js';
import { InvoiceVat } from './invoicing/invoice-vat.js';
import { TimeZoneRegion } from './time-zone-region.js';
import { EntityMixin } from '../../components/core/entity-mixin.js';
import { JSONUtils } from '../../utility/json-utils.js';
import { OrderSource } from './order-source.js';
import { OfflinePaymentDetails } from './offline-payment-details.js';
import { OrderSideEffect } from './orders/order-side-effect.js';
import { OrderSideEffectType } from './orders/order-side-effect-type.js';
import { isNotNullOrUndefined } from '../../utility/null-safety.js';
import { EventBookingStrategy } from './event-booking-protocol/event-booking-strategy.js';
import { SendProductOrderEmailSideEffect } from './orders/send-product-order-email-side-effect.js';
import { Country } from './country.js';
import { OrderDomain } from './order-domain.js';
import { CreateReceiptSideEffect } from './orders/create-receipt-side-effect.js';

export enum OnlineProductOrderStatus {
  Open = 'OPEN',
  Aborted = 'ABORTED',
  Canceled = 'CANCELED',
  Refunded = 'REFUNDED',
}

export class OnlineProductOrder extends EntityMixin(DataModel) implements LegacyCustomerOrderBase, IEntity {
  static readonly TYPE_ID: string = 'a3f4d7358b454d9d8f6469caecf46163';
  static readonly SCHEMA_VERSION: number = 15;

  public readonly isLegacyOrder = true;

  public readonly userId: string | null;
  public readonly customerId: string | null;
  public readonly language: Language | null;
  public readonly country: Country;
  public readonly timezone: TimeZoneRegion;

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string,
    public readonly eventId: string | null,
    public readonly bookingStrategy: EventBookingStrategy | null,
    public readonly source: OrderSource,
    public readonly domain: OrderDomain,
    public readonly businessRef: BusinessModuleRef,
    public readonly creationDate: dayjs.Dayjs,
    public readonly customer: CustomerProfile,
    public readonly contactDetails: ContactDetails,
    public readonly status: OnlineProductOrderStatus,
    public readonly items: ProductOrderItem[],
    public readonly paymentType: PaymentType,
    public readonly paymentMethod: PaymentMethod,
    public readonly onlinePaymentDetails: OnlinePaymentDetails | null,
    public readonly offlinePaymentDetails: OfflinePaymentDetails | null,
    public readonly components: ComponentList,
    public readonly sideEffects: OrderSideEffect[],
    public readonly isGuestOrder: boolean,
    public readonly cancelledDate: dayjs.Dayjs | null,
    public readonly cancelledBy: string | null,
    public readonly refunds: Refund[],
    public readonly donation: Price | null,
    public applicationFee: Price | null,
    public applicationFeeVat: InvoiceVat | null,
  ) {
    super();

    this.userId = customer.userId;
    this.customerId = customer.uniqueId;
    this.language = customer.language;
    this.country = contactDetails.country;
    this.timezone = contactDetails.timezone;
  }

  get $typeId(): string {
    return OnlineProductOrder.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(11, OnlineProductOrder.SCHEMA_VERSION);
  }

  get isOpen(): boolean {
    return this.status === OnlineProductOrderStatus.Open;
  }

  get isCanceled(): boolean {
    return this.status === OnlineProductOrderStatus.Canceled;
  }

  static create(args: {
    uniqueId: string,
    creatorId: string,
    eventId: string | null,
    bookingStrategy: EventBookingStrategy | null,
    source: OrderSource,
    domain: OrderDomain,
    businessRef: BusinessModuleRef,
    creationDate: dayjs.Dayjs,
    customer: CustomerProfile,
    contactDetails: ContactDetails,
    status: OnlineProductOrderStatus,
    items: ProductOrderItem[],
    paymentType: PaymentType,
    paymentMethod: PaymentMethod,
    onlinePaymentDetails: OnlinePaymentDetails | null,
    offlinePaymentDetails: OfflinePaymentDetails | null,
    components: ComponentList,
    sideEffects: OrderSideEffect[],
    isGuestOrder: boolean,
    cancelledDate: dayjs.Dayjs | null,
    cancelledBy: string | null,
    refunds: Refund[],
    donation: Price | null,
    applicationFee: Price | null,
    applicationFeeVat: InvoiceVat | null,
  }): OnlineProductOrder {
    return new OnlineProductOrder(
        args.uniqueId,
        args.creatorId,
        args.eventId,
        args.bookingStrategy,
        args.source,
        args.domain,
        args.businessRef,
        args.creationDate,
        args.customer,
        args.contactDetails,
        args.status,
        args.items,
        args.paymentType,
        args.paymentMethod,
        args.onlinePaymentDetails,
        args.offlinePaymentDetails,
        args.components,
        args.sideEffects,
        args.isGuestOrder,
        args.cancelledDate,
        args.cancelledBy,
        args.refunds,
        args.donation,
        args.applicationFee,
        args.applicationFeeVat,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): OnlineProductOrder {
    return this.create({
      uniqueId: MapUtils.getString(map, 'uniqueId'),
      creatorId: MapUtils.getString(map, 'creatorId'),
      eventId: MapUtils.getStringOrNull(map, 'eventId'),
      bookingStrategy: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'bookingStrategy'), EventBookingStrategy),
      source: toEnumFromString(MapUtils.getString(map, 'source'), OrderSource),
      domain: toEnumFromString(MapUtils.getString(map, 'domain'), OrderDomain),
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      customer: context.fromData<CustomerProfile>(MapUtils.getRecord(map, 'customer')),
      contactDetails: context.fromData<ContactDetails>(MapUtils.getRecord(map, 'contactDetails')),
      status: toEnumFromString(MapUtils.getString(map, '$status'), OnlineProductOrderStatus),
      items: MapUtils.getList<ProductOrderItem>(map, 'items', (value) => context.fromData<ProductOrderItem>(JSONUtils.decodeRecord(value))),
      paymentType: toEnumFromString(MapUtils.getString(map, 'paymentType'), PaymentType),
      paymentMethod: toEnumFromString(MapUtils.getString(map, 'paymentMethod'), PaymentMethod),
      onlinePaymentDetails: context.fromDataOrNull<OnlinePaymentDetails>(MapUtils.getRecordOrNull(map, 'onlinePaymentDetails')),
      offlinePaymentDetails: context.fromDataOrNull<OfflinePaymentDetails>(MapUtils.getRecordOrNull(map, 'offlinePaymentDetails')),
      components: ComponentList.deserialize(
          context, MapUtils.getList<SerializedData>(map, 'components', (value) => JSONUtils.decodeRecord(value)),
      ),
      sideEffects: MapUtils.getList<OrderSideEffect>(map, 'sideEffects', (value) => context.fromData<OrderSideEffect>(JSONUtils.decodeRecord(value))),
      isGuestOrder: MapUtils.getBoolean(map, 'isGuestOrder'),
      cancelledDate: context.decodeDateOrNull(MapUtils.getNumberOrNull(map, 'cancelledDate')),
      cancelledBy: MapUtils.getStringOrNull(map, 'cancelledBy'),
      refunds: MapUtils.getList<Refund>(map, 'refunds', (value) => {
        return context.fromData<Refund>(JSONUtils.decodeRecord(value));
      }),
      donation: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'donation')),
      applicationFee: Price.decodeOrNull(MapUtils.getStringOrNull(map, 'applicationFee')),
      applicationFeeVat: toEnumFromStringOrNull(MapUtils.getStringOrNull(map, 'applicationFeeVat'), InvoiceVat),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'eventId': this.eventId,
      'bookingStrategy': fromEnumAsStringOrNull(this.bookingStrategy),
      'source': fromEnumAsString(this.source),
      'domain': fromEnumAsString(this.domain),
      'customerId': this.customerId,
      'userId': this.userId,
      'businessRef': this.businessRef.encode(),
      'creationDate': context.encodeDate(this.creationDate),
      'contactDetails': context.toData(this.contactDetails),
      'customer': context.toData(this.customer),
      'items': this.items.map((e) => context.toData(e)),
      'components': this.components.serialize(context),
      'paymentType': fromEnumAsString(this.paymentType),
      'paymentMethod': fromEnumAsString(this.paymentMethod),
      'onlinePaymentDetails': context.toDataOrNull(this.onlinePaymentDetails),
      'offlinePaymentDetails': context.toDataOrNull(this.offlinePaymentDetails),
      '$status': fromEnumAsString(this.status),
      'sideEffects': this.sideEffects.map((e) => context.toData(e)),
      'isGuestOrder': this.isGuestOrder,
      'cancelledDate': context.encodeDateOrNull(this.cancelledDate),
      'cancelledBy': this.cancelledBy,
      'donation': this.donation?.encode() ?? null,
      'applicationFee': this.applicationFee?.encode() ?? null,
      'applicationFeeVat': fromEnumAsStringOrNull(this.applicationFeeVat),
      'refunds': this.refunds.map((e) => context.toData(e)),
    };
  }

  clone(): this {
    return this.copyWith() as this;
  }

  copyWith({
    uniqueId = this.uniqueId,
    creatorId = this.creatorId,
    eventId = this.eventId,
    bookingStrategy = this.bookingStrategy,
    source = this.source,
    domain = this.domain,
    businessRef = cloneData(this.businessRef),
    creationDate = TimeZone.cloneDate(this.creationDate),
    customer = cloneData(this.customer),
    contactDetails = cloneData(this.contactDetails),
    status = this.status,
    items = cloneListOfCloneable(this.items),
    paymentType = this.paymentType,
    paymentMethod = this.paymentMethod,
    onlinePaymentDetails = cloneDataOrNull(this.onlinePaymentDetails),
    offlinePaymentDetails = cloneDataOrNull(this.offlinePaymentDetails),
    components = cloneData(this.components),
    sideEffects = cloneListOfCloneable(this.sideEffects),
    isGuestOrder = this.isGuestOrder,
    cancelledDate = TimeZone.cloneDateOrNull(this.cancelledDate),
    cancelledBy = this.cancelledBy,
    refunds = cloneListOfCloneable(this.refunds),
    donation = cloneDataOrNull(this.donation),
    applicationFee = cloneDataOrNull(this.applicationFee),
    applicationFeeVat = this.applicationFeeVat,
  }: {
    uniqueId?: string,
    creatorId?: string,
    eventId?: string | null,
    bookingStrategy?: EventBookingStrategy | null,
    source?: OrderSource,
    domain?: OrderDomain,
    businessRef?: BusinessModuleRef,
    creationDate?: dayjs.Dayjs,
    customer?: CustomerProfile,
    contactDetails?: ContactDetails,
    status?: OnlineProductOrderStatus,
    items?: ProductOrderItem[],
    paymentType?: PaymentType,
    paymentMethod?: PaymentMethod,
    onlinePaymentDetails?: OnlinePaymentDetails | null,
    offlinePaymentDetails?: OfflinePaymentDetails | null,
    components?: ComponentList,
    sideEffects?: OrderSideEffect[],
    isGuestOrder?: boolean,
    cancelledDate?: dayjs.Dayjs | null,
    cancelledBy?: string | null,
    refunds?: Refund[],
    donation?: Price | null,
    applicationFee?: Price | null,
    applicationFeeVat?: InvoiceVat | null,
  } = {}): this {
    return OnlineProductOrder.create({
      uniqueId,
      creatorId,
      eventId,
      bookingStrategy,
      source,
      domain,
      businessRef,
      creationDate,
      customer,
      contactDetails,
      status,
      items,
      paymentType,
      paymentMethod,
      onlinePaymentDetails,
      offlinePaymentDetails,
      components,
      sideEffects,
      isGuestOrder,
      cancelledDate,
      cancelledBy,
      refunds,
      donation,
      applicationFee,
      applicationFeeVat,
    }) as this;
  }

  copyAsAborted(): this {
    return this.copyWith({
      status: OnlineProductOrderStatus.Aborted,
    });
  }

  copyAsCanceled(cancelledBy: string | null, cancelledDate: dayjs.Dayjs, isRefund: boolean): this {
    return this.copyWith({
      status: isRefund ? OnlineProductOrderStatus.Refunded : OnlineProductOrderStatus.Canceled,
      cancelledDate: TimeZone.cloneDateOrNull(this.cancelledDate) ?? cancelledDate,
      cancelledBy: this.cancelledBy ?? cancelledBy,
    });
  }

  copyWithPaymentDetails(paymentDetails: OnlinePaymentDetails): this {
    return this.copyWith({
      onlinePaymentDetails: paymentDetails,
    });
  }

  copyWithOfflinePaymentDetails(offlinePaymentDetails: OfflinePaymentDetails): this {
    return this.copyWith({
      offlinePaymentDetails: offlinePaymentDetails,
    });
  }

  copyWithPaymentStatus(status: PaymentStatus): this {
    return this.copyWith({
      onlinePaymentDetails: this.onlinePaymentDetails?.copyWith({ status }),
    });
  }

  copyWithPaymentMethod(paymentMethod: PaymentMethod): this {
    return this.copyWith({
      paymentMethod: paymentMethod,
    });
  }

  copyWithBusinessRef(businessRef: BusinessModuleRef): this {
    return this.copyWith({
      businessRef: cloneData(businessRef),
      customer: this.customer.copyWithBusinessRef(businessRef),
      items: this.items.map((e) => e.copyWithBusinessRef(businessRef)),
      components: this.components.copyWithBusinessRef(businessRef),
    });
  }

  assignToCustomerAccount(account: CustomerAccountBase): this {
    return this.copyWith({
      creatorId: account.uniqueId,
      customer: this.customer.assignToCustomerAccount(account),
    });
  }

  isSame(other: OnlineProductOrder): boolean {
    if(this === other) {
      return true;
    }

    return isSameData(other.customer, this.customer) &&
        isSameData(other.contactDetails, this.contactDetails) &&
        isSameData(other.onlinePaymentDetails, this.onlinePaymentDetails) &&
        isSameList(other.items, this.items, isSameData) &&
        isSameList(other.sideEffects, this.sideEffects, isSameData) &&
        other.components.isSame(this.components) &&
        other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.eventId === this.eventId &&
        other.bookingStrategy === this.bookingStrategy &&
        other.source === this.source &&
        other.domain === this.domain &&
        other.cancelledDate === this.cancelledDate &&
        other.cancelledBy === this.cancelledBy &&
        isSameData(other.donation, this.donation) &&
        isSameData(other.applicationFee, this.applicationFee) &&
        isSameData(other.businessRef, this.businessRef) &&
        other.creationDate === this.creationDate &&
        other.status === this.status &&
        other.paymentMethod === this.paymentMethod &&
        other.paymentType === this.paymentType &&
        other.isGuestOrder === this.isGuestOrder &&
        other.applicationFeeVat == this.applicationFeeVat &&
        isSameList(other.refunds, this.refunds, isSameData);
  }

  tryGetCustomer(): CustomerProfile | null {
    return this.customer;
  }

  findSideEffect(effectType: OrderSideEffectType): OrderSideEffect | null {
    for(let i = 0; i < this.sideEffects.length; i++) {
      if(this.sideEffects[i].effectType === effectType) {
        return this.sideEffects[i];
      }
    }

    return null;
  }

  hasSideEffect(effectType: OrderSideEffectType): boolean {
    return this.findSideEffect(effectType) != null;
  }

  static encodedStatusSafe(status: OnlineProductOrderStatus): string {
    return fromEnumAsString(status);
  }

  static verifyType(order: DataModel): boolean {
    return order instanceof OnlineProductOrder;
  }

  static assertType(order: DataModel): order is OnlineProductOrder {
    return order instanceof OnlineProductOrder;
  }
}

export class OnlineProductOrderBuilder extends DataModelBuilder<OnlineProductOrder> {
  build(context: SerializationContext, map: SerializedData): OnlineProductOrder {
    return OnlineProductOrder.fromMap(context, map);
  }

  optimizeForQuery(context: SerializationContext, model: OnlineProductOrder): SerializedData | null {
    let paymentStatus: PaymentStatus | null = null;

    if(model.paymentType === PaymentType.Online) {
      paymentStatus = model.onlinePaymentDetails?.status ?? null;
    }
    else {
      paymentStatus = PaymentStatus.Succeeded;
    }

    return {
      'paymentStatus': fromEnumAsStringOrNull(paymentStatus),
      'paymentType': fromEnumAsString(model.paymentType),
      'paymentMethod': fromEnumAsString(model.paymentMethod),
      'containsEventProducts': model.bookingStrategy === EventBookingStrategy.TicketsAndProducts,
    };
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    this.migrateEnum(map, 'paymentType', PaymentType, PaymentType.OnDelivery);
    this.migrateEnum(map, 'paymentMethod', PaymentMethod, PaymentMethod.Cash);
    this.migrateEnum(map, '$status', OnlineProductOrderStatus, OnlineProductOrderStatus.Open);

    // Needed for deserializing invoices with a graceful creation date! See builder of [OnlinePaymentDetails].
    const creationDate = context.decodeDate(MapUtils.getInteger(map, 'creationDate'));
    context.storage.set('orderCreationDate', creationDate);

    if(versionRange.contains(OnlineProductOrder.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(OnlineProductOrder.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In version 2 we added `onCompleteHooks` of type `OrderHook[]`.
    if(version < 2) {
      map['onCompleteHooks'] = [];
      version = 2;
    }

    // In this version parameter `paymentMethod` of type `PaymentMethod` was
    // introduced.
    if(version < 3) {
      version = 3;

      const paymentType: PaymentType = toEnumFromString(MapUtils.get<string>(map, 'paymentType'), PaymentType);
      map['paymentMethod'] = fromEnumAsString(getDefaultPaymentMethodForPaymentType(paymentType));
    }

    // In version 4 we added `isGuestOrder` of type `boolean`.
    if(version < 4) {
      map['isGuestOrder'] = false;
      version = 4;
    }

    // In this version the [cancelledDate] and [cancelledBy] fields were added
    if(version < 5) version = 5;

    // In version 6 we added `applicationFee` of type `Price | null`.
    if(version < 6) version = 6;

    // In version 7 we added `refunds` of type `Refund[]`.
    if(version < 7) version = 7;

    // In this version parameter `applicationFeeVat` of type `InvoiceVat | null` was introduced.
    if(version < 8) version = 8;

    // Version 9:
    // - Add `source` of type `OrderSource`.
    if(version < 9) {
      const onlinePaymentDetails = context.fromDataOrNull<OnlinePaymentDetails>(MapUtils.getRecordOrNull(map, 'onlinePaymentDetails'));
      MapUtils.setIfNullOrUndefined(
          map,
          'source',
          fromEnumAsString(onlinePaymentDetails == null ? OrderSource.Admin : OrderSource.Client));

      version = 9;
    }

    // Version 10:
    // - Add `offlinePaymentDetails` of type `OfflinePaymentDetails | null`.
    if(version < 10) version = 10;

    if(version < 11) {
      const onCompleteHooks = MapUtils.getList<LegacyOrderHook>(map, 'onCompleteHooks', (value) => {
        return context.fromData<LegacyOrderHook>(JSONUtils.decodeRecord(value));
      });

      const sideEffects = onCompleteHooks.map((e) => e.toSideEffect()).filter(isNotNullOrUndefined);

      map['sideEffects'] = sideEffects.map((e) => context.toData(e));
      version = 11;
    }

    // Version 12:
    // - Add `eventId` of type `string | null`.
    // - Add `bookingStrategy` of type `EventBookingStrategy | null`.
    if(version < 12) {
      const source = toEnumFromString(MapUtils.getString(map, 'source'), OrderSource);
      let eventId: string | null = null;
      let bookingStrategy: EventBookingStrategy | null = null;

      if(source === OrderSource.Client) {
        const sideEffects = MapUtils.getList<OrderSideEffect>(
            map, 'sideEffects',
            (value) => context.fromData<OrderSideEffect>(JSONUtils.decodeRecord(value))
        );

        for(const se of sideEffects) {
          if(SendProductOrderEmailSideEffect.assertType(se)) {
            eventId = se.eventId;
            break;
          }
        }

        bookingStrategy = eventId != null ? EventBookingStrategy.Vouchers : null;
      }

      map['eventId'] = eventId;
      map['bookingStrategy'] = fromEnumAsStringOrNull(bookingStrategy);

      version = 12;
    }

    // In version 13 we added `donation` of type `Price | null`.
    if(version < 13) version = 13;

    // In version 14 we added `domain` of type `OrderDomain`.
    if(version < 14) {
      map['domain'] = fromEnumAsString(OrderDomain.inTime);
      version = 14;
    }

    // In version 15 we added the `CreateReceipt` side effect.
    if(version < 15) {
      const offlinePaymentDetails = context.fromDataOrNull<OfflinePaymentDetails>(MapUtils.getRecordOrNull(map, 'offlinePaymentDetails'));
      if(offlinePaymentDetails != null) {
        const sideEffects = MapUtils.getList<OrderSideEffect>(
            map, 'sideEffects',
            (value) => context.fromData<OrderSideEffect>(JSONUtils.decodeRecord(value))
        );

        sideEffects.push(CreateReceiptSideEffect.create());

        map['sideEffects'] = sideEffects.map((e) => context.toData(e));
      }

      version = 15;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
